import Slice from 'scripting/Slice';
import EmacsScripting from './EmacsScripting.js';
import ViScripting from './ViScripting.js';
import {addEffect} from './commands/effects.js';

class Scripting {

    constructor(sequencerEngine) {
        this.sequencerEngine = sequencerEngine;
        this.emacsScripting = new EmacsScripting(this.sequencerEngine);
        this.viScripting = new ViScripting(this.sequencerEngine);
        this.cursor = sequencerEngine.cursor;
        this.clipboard = [];

        // global space for users to define functions in
        this.cmd = {}

        // scope for commands to be triggered when a step arised
        // is a map from number => list of funcitons that do something to a specific step
        this.stepFunctions = {};

        // map to step and how many times we've reached that step
        this.stepTimes = {};

        this.commandHistory = [];

        this.historyCounter = 0;

        // list of future calls to be made
        // each element is an object containing how many more measures need
        // to be called. Once it becomes 0, then we call the function stored
        // in this object
        this.futureCalls = [];
    }

    viCommand(key) {
        let hasCurrentCommand =
            this.viScripting.currentCommand !== undefined;
        this.viScripting.run(key);
        return hasCurrentCommand;
    }

    emacsCommand(cmd) {
        this.emacsScripting.run(cmd);
    }

    isEmacsCommand() {
        return this.sequencerEngine.state.commands.commandPrefix === 'M-x';
    }

    runScript(str) {
        if (this.isEmacsCommand()) {
            this.emacsScripting.run(str);
            return true;
        }
        try {
            this.commandHistory.push({cursor: this.cursor.getJson(), command: str, enabled: true});
            if (str.startsWith("/")) {
                this.search(str.slice(1));
            } else if (str.startsWith("+")) {
                this.addCommand(str.slice(1));
            } else if (str.startsWith(":")) {
                return this.emacsScripting.run(str.slice(1));
            } else {
                let x = evalInContext(str, this);
            }
            this.historyCounter = 0;
            return true;
        } catch(error) {
            if (this.onFlashInfo !== undefined) {
                this.onFlashInfo(error.message);
            }
            return false; 
        }
    }

    runEmacsCommand(command) {
    }

    search(text) {
        let replace = text.endsWith("/");
        if (replace) {
            text = text.slice(0, text.length - 1);
        }
        let searchTerms = text.split("+");
        this.sequencerEngine.searchForSounds(searchTerms, replace);
    }

    addCommand(str) {
        addEffect(this.sequencerEngine, str);
    }

    getJson() {
        return {
            commandHistory: this.commandHistory.filter(cmd => cmd.enabled)
        };
    }

    fromJson(json) {
        let list = json['commandHistory'];
        for (let i=0; i < list.length; i++) {
            let cmd = null;
            if (typeof(list[i]) === 'string') {
                this.cursor.step = 0;
                this.cursor.track = 0;
                cmd = list[i];
            } else if ('cursor' in list[i]) {
                let cursor = list[i].cursor;
                this.cursor.step = cursor.step;
                this.cursor.track = cursor.track;
                cmd = list[i].command;
            }
            this.runScript(cmd);
        }
    }

    recordMix(recordingMix) {
        this.sequencerEngine.recordingMix = recordingMix;
    }
        
    // the following methods are public to the user to use in scripts:
    
    slice(i = 0) {
        let track = this.sequencerEngine.getSelectedTrack();
        let sampler = track.getSamplers()[0];
        return new Slice(sampler, i);
    }
    
    midiFX() {
        let track = this.sequencerEngine.getSelectedTrack();
        let midiFX = this.sequencrerEngine.midiEffects;

        return () => {
            let newTrack = this.sequencerEngine.getSelectedTrack();
        };
    }

    // moves cursor left in the resolution
    left() {
        this.sequencerEngine.left();
    }

    // moves cursor right one step in the resolution
    right() {
        this.sequencerEngine.right();
    }

    // moves cursor up a track
    up() {
        this.sequencerEngine.up();
    }

    // moves cursor down a track
    down() {
        this.sequencerEngine.down();
    }

    arp(enabled) {
        let track = this.sequencerEngine.getSelectedTrack();
        track.trackData.arpOn = enabled;
    }

    arpRate(num, triplets) {
        let track = this.sequencerEngine.getSelectedTrack();
        //track.trackData.arpRate = new Resolution(num, triplets);
    }

    arpOctaves(octaves) {
        let track = this.sequencerEngine.getSelectedTrack();
        track.trackData.arpOctaves = octaves;
    }

    // returns numSteps many steps starting at the cursor
    steps(numSteps) {
        let track = this.sequencerEngine.getSelectedTrack();
        let steps = track.getStepsAtResolution(this.sequencerEngine.resolution);
        if (numSteps === undefined) {
            numSteps = steps.length;
        }

        if (numSteps + this.sequencerEngine.getSelectedStep() >= steps.length) {
            numSteps -= numSteps + this.sequencerEngine.getSelectedStep() - steps.length;
        }
        let selectedStep = this.sequencerEngine.getSelectedStep();

        return new ScriptableSteps(this.sequencerEngine, track, steps.slice(
            selectedStep,
            selectedStep + numSteps),
            selectedStep);
    }

    // copies numSteps many steps starting at the cursor into
    // the clipboard, returning the clipboard as a list of steps
    // if its more than 1, or as 1 step if theres only 1
    copy(numSteps=1) {
        this.clipboard = this.steps(numSteps)
            .map(step => step.getCopy());

        return numSteps=1 ? this.clipboard[0] : this.clipboard;
    }

    // pastes the clipboard into steps starting at the cursor
    paste() {
        let steps = this.steps(this.clipboard.length).stepDataList;
        for (let i=0; i < steps.length; i++) {
            steps[i].copyFrom(this.clipboard[i]);
        }

        this.sequencerEngine
            .getSelectedTrack()
            .syncBuckets(this.sequencerEngine.resolution);

        this.sequencerEngine
            .getSelectedTrack()
            .updateSteps();
    }

    rand() {
        return Math.random();
    }

    // called by sequencer when a given a step is reached
    // this function then calls whatever functions then user
    // has set up to be called at that moment
    onStepReached(step) {
        if (step in this.stepFunctions) {
            try {
                this.stepFunctions[step].map(x => x());
            } catch (error) {
                if (this.onFlashInfo !== undefined) {
                    this.onFlashInfo(error.message);
                }
            }
            this.stepTimes[step]++;
        }
    }

    // utility function for user to directly define a function to be
    // run for a given step, each time it is reached
    stepFunction(cmd) {
        let copied = this.steps().get(0);
        let track = this.sequencerEngine
            .getSelectedTrack();
        let step = this.cursor.step;
        let stepFunc = () => {
            cmd(copied, this.stepTimes[step]);
        }
        if (step in this.stepFunctions) {
            this.stepFunctions[step].push(stepFunc);
        } else {
            this.stepFunctions[step] = [stepFunc];
            this.stepTimes[step] = 0;
        }
    }

    volume(i) {
        let track = this.sequencerEngine.getSelectedTrack();
        track.setRawVolume(i);
    }

    // goes to beginning of selected track
    start() {
        this.cursor.step = 0;
    }

    // goes to end of selected track
    end() {
        let length = this.sequencerEngine.getTrackLength();
        this.cursor.setStep(length - 1, this.sequencerEngine.resolution);
    }

    line(steps) {
        this.steps(steps).map(() => { this.right(); });
    }

    // clears all step functions starting at cursor to numsteps many steps
    clearStepFunctions(numSteps = 1) {
        for (let i=0; i < numSteps; i++) {
            let step = this.cursor.step + i;
            delete this.stepFunctions[step];
        }
    }

    // goes to pattern # i
    pattern(i) {
        this.sequencerEngine.switchToPattern(i);
    }

    clearSequence() {
        this.sequencerEngine.clearSequence();
    }

    clear() {
        let track = this.sequencerEngine
            .getSelectedTrack();
        track.pattern.clear();
    }

    upHistory() {

        let index = this.commandHistory.length - this.historyCounter - 1;
        let cmd = this.commandHistory[index];
        this.historyCounter = Math.min((this.historyCounter + 1) , this.commandHistory.length);
        return cmd === undefined ? '' : cmd.command;
    }

    downHistory() {
        this.historyCounter--;
        if (this.historyCounter <= 0) {
            this.historyCounter = 0;
            return '';
        }
        let cmd = this.commandHistory[this.commandHistory.length - this.historyCounter].command;
        return cmd;
    }

    // should be called when we delete a track
    deleteHistoryForTrack(trackNumber) {
        let commandHistory = [];
        for (let i=0; i < this.commandHistory.length; i++) {
            if (this.commandHistory[i].cursor.track != trackNumber) {
                commandHistory.push(this.commandHistory[i]);
            }
        }
        this.commandHistory = commandHistory;
    }

    get currentStep() {
        return this.sequencerEngine.trackStep;
    }

    map(func) {
        this.sequencerEngine.stepNumberScript = func;
    }

    get phi() {
        return 1.61803398875;
    }

    com(... a) {
        return (x) => {
            let val = x;
            for (let i=a.length - 1; i >= 0; i--) {
                val = a[i](val);
            }
            return val;
        };
    }
}

function evalInContext(scr, context)
{
    // execute script in private context
    return (new Function( "with(this) { " + scr + "}")).call(context);
}

class ScriptableSteps extends Scripting {
    constructor(sequencerEngine, track, stepDataList, step, stepOffset=1) {
        super(sequencerEngine);
        this.stepDataList = stepDataList;
        this.step = step;
        this.stepOffset = stepOffset;
        this.track = track;
    }

    filter(cond) {
        return new ScriptableSteps(this.sequencerEngine, this.track, this.stepDataList.filter(cond), this.step);
    }

    isOn() {
        return this.filter((x) => x.on);
    }

    sync() {
        this.sequencerEngine
            .getSelectedTrack()
            .syncBuckets(this.sequencerEngine.resolution);

        this.track.updateSteps();
    }

    get(i) {
        return this.stepDataList[i];
    }

    even() {
        let list = [];
        for (let i=0; i < this.stepDataList.length; i+=2) {
            list.push(this.stepDataList[i]);
        }
        return new ScriptableSteps(this.sequencerEngine, this.track, list, this.step, this.stepOffset * 2);
    }

    odd() {
        let list = [];
        for (let i=1; i < this.stepDataList.length; i+=2) {
            list.push(this.stepDataList[i]);
        }

        let addition = this.sequencerEngine.resolution.transformStepAtBaseResolution(1);
        return new ScriptableSteps(this.sequencerEngine, this.track, list, this.step + addition, this.stepOffset * 2);
    }

    stepFunction(f) {
        this.do(() => this.sequencerEngine.scripting.stepFunction(f));
    }

    // run the f on each step, with the cursor pointing to that step on each call
    do(f) {
        let cursor = this.sequencerEngine.cursor;
        let cursorStep = cursor.step;
        let stepNumber = this.step;
        for (let stepIndex in this.stepDataList) {
            cursor.step = stepNumber;
            f();
            stepNumber += this.sequencerEngine.resolution.transformStepAtBaseResolution(this.stepOffset);
        }
        cursor.step = cursorStep;
    }

    set on(on) {
        this.map((x, i) => x.on = typeof(on) === 'function' ? on(i) : on);
        this.sync();
    }

    get on() {
        return this.map((x) => x.on);
    }

    set duration(duration) {
        this.map((x, i ) => x.duration = typeof(duration) === 'function' ? duration(i) : duration);
        this.sync();
    }

    get duration() {
        return this.map((x) => x.duration);
    }

    set chop(chop) {
        this.map((x, i) => x.chop = typeof(chop) === 'function' ? chop(i) : chop);
        this.sync();
    }

    get chop() {
        return this.map((x) => x.chop);
    }

    set velocity(velocity) {
        this.map((x, i) => x.velocity= typeof(velocity) === 'function' ? velocity(i) : velocity);
        this.sync();
    }

    get velocity() {
        return this.map((x) => x.velocity);
    }

    set transpose(transpose) {
        this.map((x, i) => x.transpose = typeof(transpose) === 'function' ? transpose(i) : transpose);
        this.sync();
    }

    get transpose() {
        return this.map((x) => x.transpose);
    }

    set attack(attack) {
        this.map((x, i) => x.transpose= typeof(attack) === 'function' ? attack(i) : attack);
        this.sync();
    }

    get attack() {
        return this.map((x) => x.attack);
    }

    set release(release) {
        this.map((x, i) => x.transpose= typeof(release) === 'function' ? release(i) : release);
        this.sync();
    }

    get release () {
        return this.map((x) => x.release);
    }

    map(f) {
        let x = this.stepDataList.map(f);
        this.sync();
        return x;
    }
}


export default Scripting;
